import type { CreateEnvironmentInput, EnvironmentDto, UpdateEnvironmentInput } from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export const environmentsQueryOptions = (projectCode: string) =>
  queryOptions({
    queryKey: queryKeys.environments.list(projectCode),
    queryFn: ({ signal }) =>
      api.get<EnvironmentDto[]>(`/projects/${projectCode}/environments`, undefined, signal),
  });

/** `enabled` lets a closed dialog mount its picker without fetching. */
export const useEnvironments = (projectCode: string, options: { enabled?: boolean } = {}) =>
  useQuery({ ...environmentsQueryOptions(projectCode), enabled: options.enabled ?? true });

/**
 * An environment is named inside cases, checklists and runs, so renaming or
 * removing one changes what all three display.
 */
const useEnvironmentInvalidation = (projectCode: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.environments.list(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.testCases.all(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.checklists.all(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.testRuns.all(projectCode) });
  };
};

export const useCreateEnvironment = (projectCode: string) => {
  const invalidate = useEnvironmentInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: CreateEnvironmentInput) =>
      api.post<EnvironmentDto>(`/projects/${projectCode}/environments`, input),
    onSuccess: invalidate,
  });
};

export const useUpdateEnvironment = (projectCode: string) => {
  const invalidate = useEnvironmentInvalidation(projectCode);
  return useMutation({
    mutationFn: ({
      environmentId,
      input,
    }: {
      environmentId: string;
      input: UpdateEnvironmentInput;
    }) =>
      api.patch<EnvironmentDto>(`/projects/${projectCode}/environments/${environmentId}`, input),
    onSuccess: invalidate,
  });
};

export const useDeleteEnvironment = (projectCode: string) => {
  const invalidate = useEnvironmentInvalidation(projectCode);
  return useMutation({
    mutationFn: (environmentId: string) =>
      api.delete(`/projects/${projectCode}/environments/${environmentId}`),
    onSuccess: invalidate,
  });
};
